/**
 * Single source of truth for brand tokens. The web app maps these onto Ant
 * Design tokens (apps/web/src/theme); the mobile app mirrors them in Compose
 * (apps/mobile/shared/src/commonMain/.../shared/ui/Theme.kt). Logos live in ../../assets/logo
 * (generated from master.svg by `make sync-logos`). A second company's
 * branding is a new token set, not a restyle (docs/technical-design.md →
 * Reuse seams).
 *
 * Every text/background pair below meets WCAG AA (≥4.5:1), asserted by
 * brand.spec.ts — a palette change that breaks contrast fails the build.
 */

export const COLOR_MODES = ['light', 'dark'] as const;
export type ColorMode = (typeof COLOR_MODES)[number];

/** Fixed logo colours from the logo sheet — identical in both modes. UI surfaces use the palettes below. */
export const BRAND_BLUE = '#2E64EB';
export const BRAND_NAVY = '#09101F';

export interface Palette {
  /** Filled buttons, active states. White text on it. */
  primary: string;
  /** Links and blue text — separate because the button blue is too dim as text on dark surfaces. */
  link: string;
  /** Decorative only (highlights, charts) — not guaranteed AA as text. */
  accent: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  /** Page background behind cards and tables. */
  background: string;
  /** Cards, tables, header. */
  surface: string;
  text: string;
  textMuted: string;
  border: string;
  /** Navigation rail. */
  sider: string;
  siderSelected: string;
  siderSelectedText: string;
}

/**
 * Soft, warm palette: cream paper neutrals with muted earth tones (sage,
 * ochre, terracotta) for status, and a softened brand blue for action. All
 * colours are low-saturation so nothing shouts; blue is the only cool hue, so
 * it still reads as "the thing to click". The logo keeps BRAND_BLUE.
 */
const UI_BLUE = '#3D63C9';

const light: Palette = {
  primary: UI_BLUE,
  link: '#3A5EBF',
  accent: '#C8764F',
  success: '#4F7A3E',
  warning: '#8E6118',
  error: '#B24A36',
  info: UI_BLUE,
  background: '#F7F3EA',
  surface: '#FFFDF8',
  text: '#2B2722',
  textMuted: '#6B6357',
  border: '#E6DFD1',
  sider: '#FFFDF8',
  siderSelected: '#E8ECF6',
  siderSelectedText: '#3355B0',
};

/**
 * Dark: warm charcoal (a hint of brown, never blue) with cream text. Status
 * colours are the light set lifted to pastel so they glow softly instead of
 * vibrating. Filled buttons keep the UI blue (white text 5.5:1); blue text uses
 * the lighter link blue (7.4:1 on surface).
 */
const dark: Palette = {
  primary: UI_BLUE,
  link: '#93ABEB',
  accent: '#E0A07A',
  success: '#98B57F',
  warning: '#E2B462',
  error: '#E88B75',
  info: '#93ABEB',
  background: '#161513',
  surface: '#1F1E1B',
  text: '#ECE6DA',
  textMuted: '#A69F92',
  border: '#34322D',
  sider: '#1A1917',
  siderSelected: '#2D2B27',
  siderSelectedText: '#F5EFE3',
};

export const BRAND = {
  name: 'Chukua',
  domain: 'chukua.app',
  palettes: { light, dark } satisfies Record<ColorMode, Palette>,
  /** Logo colours for brand moments (login panel, splash) — same in both modes. */
  logo: { blue: BRAND_BLUE, navy: BRAND_NAVY },
  radius: 8,
  /**
   * Same stacks as dev-tracker and nestyclass. Self-hosted: web via fontsource
   * (DM Sans Variable wght + DM Mono 400/500), driver via res/font.
   */
  fontFamily: "'DM Sans Variable', 'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  /** Truck registrations, job/invoice numbers, eTIMS control numbers, IDs. */
  fontFamilyMono: "'DM Mono', ui-monospace, 'SF Mono', 'SFMono-Regular', Menlo, Consolas, monospace",
} as const;

export type Brand = typeof BRAND;
